import { Button } from '~/components/common/Button'
import { CopySnippet } from '~/components/common/CopySnippet'
import { InputArea } from '~/components/common/InputArea'

import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { Selector, SelectItem } from '~/components/common/Selector'
import { UploadFile, type UploadState } from '~/components/common/UploadFile'
import { ConnectionType } from '~/constants/connections'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { useOAuthPopup } from '~/hooks/useOAuthPopup'
import { getCustomConnectionConfig, updateCustomConnection } from '~/services/organizationService'
import store from '~/store'
import { setConnType } from '~/store/connectionSlice'
import { CustomConnectionParam } from '~/types/ResponseType'

export enum CustomIntegrationConnType {
  OAuth = 'OAuth'
}

interface CustomIntegrationFormProps {
  onCancel: () => void
  onOAuthSuccess?: (connectionId: string) => void
  connectedConnList: string[]
  initialConnType?: ConnectionType
}

interface CustomIntegrationOauthFormProps {
  isLoading: boolean
  customIntegration: CustomConnectionParam
  setCustomIntegration: React.Dispatch<React.SetStateAction<CustomConnectionParam>>
}

export function CustomIntegrationForm({
  onCancel,
  connectedConnList,
  initialConnType,
  onOAuthSuccess
}: CustomIntegrationFormProps) {
  const { origin } = new URL(location.href)
  const { currentWorkspace } = useWorkspaceContext()
  const { openOAuthPopup } = useOAuthPopup()

  const getRedirectUrl = useCallback(
    (connType: string) => `${origin}/oauth/${connType}/callback`,
    [origin]
  )

  const [customIntegrationModalLoading, setCustomIntegrationModalLoading] = useState(false)

  const [_customIntegrationConnType, setCustomIntegrationConnType] = useState(
    CustomIntegrationConnType.OAuth.toString()
  )
  const defaultConnType = initialConnType || ConnectionType.SelfHostedGitlab
  const [customIntegration, setCustomIntegration] = useState<CustomConnectionParam>({
    connType: defaultConnType,
    serverUrl: '',
    clientId: '',
    clientSecret: '',
    redirectUrl: getRedirectUrl(defaultConnType),
    apiUrl: '',
    apiKey: '',
    appName: '',
    verifySsl: true,
    appId: '',
    privateKey: '',
    hasPrivateKey: false,
    serverCaCertBase64: '',
    hasServerCaCert: false
  })
  const [initialHasServerCaCert, setInitialHasServerCaCert] = useState(false)
  const [caCertUploadState, setCaCertUploadState] = useState<UploadState>({ status: 'default' })
  const [caCertError, setCaCertError] = useState<string>('')
  const [privateKeyUploadState, setPrivateKeyUploadState] = useState<UploadState>({
    status: 'default'
  })
  const [privateKeyError, setPrivateKeyError] = useState<string>('')

  const connectedConnTypeSet = useMemo(() => {
    const normalizedConnections = connectedConnList.map(conn => conn.toLowerCase())
    const normalizedSet = new Set(normalizedConnections)

    if (normalizedSet.has('github_custom')) {
      normalizedSet.add(ConnectionType.SelfHostedGithub.toLowerCase())
    }
    if (normalizedSet.has(ConnectionType.SelfHostedGithub.toLowerCase())) {
      normalizedSet.add('github_custom')
    }
    if (normalizedSet.has(ConnectionType.GitLab.toLowerCase())) {
      normalizedSet.add(ConnectionType.SelfHostedGitlab.toLowerCase())
    }
    if (normalizedSet.has(ConnectionType.SelfHostedGitlab.toLowerCase())) {
      normalizedSet.add(ConnectionType.GitLab.toLowerCase())
    }

    return normalizedSet
  }, [connectedConnList])

  const fetchedConnTypesRef = useRef(new Set<string>())

  const resetCustomIntegrationState = useCallback(
    (connType: string) => {
      setInitialHasServerCaCert(false)
      setCaCertUploadState({ status: 'default' })
      setCaCertError('')
      setPrivateKeyUploadState({ status: 'default' })
      setPrivateKeyError('')
      setCustomIntegration({
        connType,
        serverUrl: '',
        clientId: '',
        clientSecret: '',
        redirectUrl: getRedirectUrl(connType),
        apiUrl: '',
        apiKey: '',
        appName: '',
        verifySsl: true,
        appId: '',
        privateKey: '',
        hasPrivateKey: false,
        serverCaCertBase64: '',
        hasServerCaCert: false
      })
      setCustomIntegrationConnType(CustomIntegrationConnType.OAuth)
      setCustomIntegrationModalLoading(false)
    },
    [getRedirectUrl]
  )

  const getCustomIntegration = useCallback(
    async (connType: string) => {
      setCustomIntegrationModalLoading(true)
      setCustomIntegration(prev => ({
        ...prev,
        connType
      }))

      try {
        if (!currentWorkspace?.uuid) {
          throw new Error('No workspace selected')
        }
        const data = await getCustomConnectionConfig(currentWorkspace.uuid, connType)
        const normalizedConnType =
          data.conn_type === ConnectionType.GitHub && data.is_self_hosted
            ? ConnectionType.SelfHostedGithub
            : data.conn_type === 'github_custom'
              ? ConnectionType.SelfHostedGithub
              : data.conn_type
        const hasServerCaCert = Boolean(data.has_server_ca_cert)

        setInitialHasServerCaCert(hasServerCaCert)
        setCaCertUploadState({ status: 'default' })
        setCaCertError('')
        setPrivateKeyUploadState({ status: 'default' })
        setPrivateKeyError('')

        setCustomIntegration({
          connType: normalizedConnType,
          serverUrl: data.server_url,
          clientId: data.client_id,
          clientSecret: data.client_secret,
          redirectUrl: data.redirect_uri || getRedirectUrl(connType),
          apiUrl: data.api_url || '',
          apiKey: data.api_key,
          appName: data.app_name || '',
          verifySsl: data.verify_ssl !== undefined ? data.verify_ssl : true,
          appId: data.app_id || '',
          privateKey: '',
          hasPrivateKey: data.has_private_key ?? false,
          serverCaCertBase64: '',
          hasServerCaCert: hasServerCaCert
        })
        setCustomIntegrationConnType(CustomIntegrationConnType.OAuth)
      } catch (_error) {
        resetCustomIntegrationState(connType)
      } finally {
        setCustomIntegrationModalLoading(false)
      }
    },
    [getRedirectUrl, resetCustomIntegrationState, currentWorkspace]
  )

  const handleConnectionSelection = useCallback(
    (value: string) => {
      if (!value) {
        return
      }
      fetchedConnTypesRef.current.delete(value.toLowerCase())
      if (!connectedConnTypeSet.has(value.toLowerCase())) {
        resetCustomIntegrationState(value)
        return
      }
      void getCustomIntegration(value)
    },
    [connectedConnTypeSet, getCustomIntegration, resetCustomIntegrationState]
  )

  useEffect(() => {
    const connType = customIntegration.connType
    const normalizedConnType = connType.toLowerCase()

    const linkedConnTypes =
      normalizedConnType === ConnectionType.SelfHostedGithub.toLowerCase()
        ? [ConnectionType.SelfHostedGithub.toLowerCase(), 'github_custom']
        : normalizedConnType === 'github_custom'
          ? [ConnectionType.SelfHostedGithub.toLowerCase(), 'github_custom']
          : normalizedConnType === ConnectionType.SelfHostedGitlab.toLowerCase()
            ? [ConnectionType.SelfHostedGitlab.toLowerCase(), ConnectionType.GitLab.toLowerCase()]
            : [normalizedConnType]

    const isConnected = linkedConnTypes.some(conn => connectedConnTypeSet.has(conn))

    if (!isConnected) {
      fetchedConnTypesRef.current.delete(normalizedConnType)
      return
    }

    if (fetchedConnTypesRef.current.has(normalizedConnType)) {
      return
    }

    fetchedConnTypesRef.current.add(normalizedConnType)
    void getCustomIntegration(connType)
  }, [connectedConnTypeSet, customIntegration.connType, getCustomIntegration])

  const convertArrayBufferToBase64 = (buffer: ArrayBuffer): string => {
    const bytes = new Uint8Array(buffer)
    const chunkSize = 0x8000
    let binary = ''
    for (let i = 0; i < bytes.byteLength; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize)
      binary += String.fromCharCode(...chunk)
    }
    return window.btoa(binary)
  }

  const processCaCertFile = (file: File) => {
    setCaCertError('')

    const extension = file.name.split('.').pop()?.toLowerCase()
    const allowedExtensions = ['pem', 'crt', 'cer']
    if (extension && !allowedExtensions.includes(extension)) {
      const errorMessage = 'Please upload a PEM/CRT certificate file.'
      setCaCertUploadState({
        status: 'error',
        error: errorMessage,
        file: { name: file.name, size: file.size }
      })
      setCustomIntegration(prev => ({
        ...prev,
        serverCaCertBase64: '',
        hasServerCaCert: initialHasServerCaCert
      }))
      setCaCertError(errorMessage)
      return
    }

    setCaCertUploadState({
      status: 'uploading',
      file: { name: file.name, size: file.size }
    })

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const result = reader.result
        let base64Value = ''
        if (typeof result === 'string') {
          base64Value = window.btoa(result)
        } else if (result instanceof ArrayBuffer) {
          base64Value = convertArrayBufferToBase64(result)
        } else {
          throw new Error('Unsupported certificate format')
        }

        setCustomIntegration(prev => ({
          ...prev,
          serverCaCertBase64: base64Value,
          hasServerCaCert: true
        }))
        setCaCertUploadState({
          status: 'uploaded',
          file: { name: file.name, size: file.size }
        })
        setCaCertError('')
      } catch (_error) {
        const errorMessage = 'Failed to process certificate file.'
        setCustomIntegration(prev => ({
          ...prev,
          serverCaCertBase64: '',
          hasServerCaCert: initialHasServerCaCert
        }))
        setCaCertUploadState({
          status: 'error',
          error: errorMessage,
          file: { name: file.name, size: file.size }
        })
        setCaCertError(errorMessage)
      }
    }

    reader.onerror = () => {
      const errorMessage = 'Failed to read certificate file.'
      setCustomIntegration(prev => ({
        ...prev,
        serverCaCertBase64: '',
        hasServerCaCert: initialHasServerCaCert
      }))
      setCaCertUploadState({
        status: 'error',
        error: errorMessage,
        file: { name: file.name, size: file.size }
      })
      setCaCertError(errorMessage)
    }

    reader.readAsArrayBuffer(file)
  }

  const handleCaCertSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    const files = input.files
    if (!files || files.length === 0) {
      input.value = ''
      return
    }

    processCaCertFile(files[0])
    input.value = ''
  }

  const handleCaCertDrop = (file: File) => {
    processCaCertFile(file)
  }

  const handleCaCertRemove = () => {
    setCustomIntegration(prev => ({
      ...prev,
      serverCaCertBase64: '',
      hasServerCaCert: initialHasServerCaCert
    }))
    setCaCertUploadState({ status: 'default' })
    setCaCertError('')
  }

  const processPrivateKeyFile = (file: File) => {
    setPrivateKeyError('')

    const extension = file.name.split('.').pop()?.toLowerCase()
    const allowedExtensions = ['pem', 'key']
    if (extension && !allowedExtensions.includes(extension)) {
      const errorMessage = 'Please upload a PEM private key file.'
      setPrivateKeyUploadState({
        status: 'error',
        error: errorMessage,
        file: { name: file.name, size: file.size }
      })
      setCustomIntegration(prev => ({
        ...prev,
        privateKey: ''
      }))
      setPrivateKeyError(errorMessage)
      return
    }

    setPrivateKeyUploadState({
      status: 'uploading',
      file: { name: file.name, size: file.size }
    })

    const reader = new FileReader()
    reader.onload = () => {
      try {
        const result = reader.result
        let privateKeyContent = ''
        if (typeof result === 'string') {
          privateKeyContent = result
        } else if (result instanceof ArrayBuffer) {
          const decoder = new TextDecoder('utf-8')
          privateKeyContent = decoder.decode(result)
        } else {
          throw new Error('Unsupported private key format')
        }

        // Validate that it looks like a PEM private key
        if (!privateKeyContent.includes('BEGIN') || !privateKeyContent.includes('PRIVATE KEY')) {
          throw new Error('Invalid private key format')
        }

        setCustomIntegration(prev => ({
          ...prev,
          privateKey: privateKeyContent
        }))
        setPrivateKeyUploadState({
          status: 'uploaded',
          file: { name: file.name, size: file.size }
        })
        setPrivateKeyError('')
      } catch (_error) {
        const errorMessage =
          "Failed to process private key file. Please ensure it's a valid PEM format."
        setCustomIntegration(prev => ({
          ...prev,
          privateKey: ''
        }))
        setPrivateKeyUploadState({
          status: 'error',
          error: errorMessage,
          file: { name: file.name, size: file.size }
        })
        setPrivateKeyError(errorMessage)
      }
    }

    reader.onerror = () => {
      const errorMessage = 'Failed to read private key file.'
      setCustomIntegration(prev => ({
        ...prev,
        privateKey: ''
      }))
      setPrivateKeyUploadState({
        status: 'error',
        error: errorMessage,
        file: { name: file.name, size: file.size }
      })
      setPrivateKeyError(errorMessage)
    }

    reader.readAsText(file)
  }

  const handlePrivateKeySelect = (event: ChangeEvent<HTMLInputElement>) => {
    const input = event.target
    const files = input.files
    if (!files || files.length === 0) {
      input.value = ''
      return
    }

    processPrivateKeyFile(files[0])
    input.value = ''
  }

  const handlePrivateKeyDrop = (file: File) => {
    processPrivateKeyFile(file)
  }

  const handlePrivateKeyRemove = () => {
    setCustomIntegration(prev => ({
      ...prev,
      privateKey: ''
    }))
    setPrivateKeyUploadState({ status: 'default' })
    setPrivateKeyError('')
  }

  return (
    <>
      <div className='w-full h-[calc(100vh-160px-120px-64px-68px)] p-12 flex flex-col justify-start items-center overflow-y-auto'>
        <div className='w-[360px] h-[71px] mb-4'>
          <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
            Platform
          </div>
          <Selector
            className='w-full h-12'
            size='default'
            placeholder='Select input format'
            disabled={customIntegrationModalLoading}
            value={customIntegration.connType}
            onSelectionChange={(value: string) => {
              handleConnectionSelection(value)
            }}
          >
            <SelectItem
              key={ConnectionType.SelfHostedGitlab}
              value={ConnectionType.SelfHostedGitlab}
            >
              Self Hosted Gitlab
            </SelectItem>
            <SelectItem
              key={ConnectionType.SelfHostedGithub}
              value={ConnectionType.SelfHostedGithub}
            >
              Self Hosted GitHub
            </SelectItem>
          </Selector>
        </div>
        <div className='w-[360px] mb-[60px]'>
          <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
            Server Url
          </div>
          <InputArea
            singleLine
            placeholder={
              customIntegration.connType === ConnectionType.SelfHostedGithub
                ? 'e.g. https://ghe.example.com'
                : 'e.g. https://gitlab.example.com'
            }
            className='w-full h-12 py-[8px]'
            isDisabled={customIntegrationModalLoading}
            value={customIntegration.serverUrl}
            onChange={e =>
              setCustomIntegration({
                ...customIntegration,
                serverUrl: e.target.value
              })
            }
          />

          {customIntegration.connType === ConnectionType.SelfHostedGithub && (
            <div className='mt-6'>
              <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
                GitHub App Name
              </div>
              <InputArea
                singleLine
                fullWidth
                placeholder='e.g. curie-enterprise-app'
                isDisabled={customIntegrationModalLoading}
                value={customIntegration.appName}
                onChange={e =>
                  setCustomIntegration({
                    ...customIntegration,
                    appName: e.target.value
                  })
                }
              />
            </div>
          )}
          {customIntegration.connType === ConnectionType.SelfHostedGithub && (
            <div className='mt-6'>
              <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
                GitHub App ID
              </div>
              <InputArea
                singleLine
                fullWidth
                placeholder='e.g. 123456'
                isDisabled={customIntegrationModalLoading}
                value={customIntegration.appId}
                onChange={e =>
                  setCustomIntegration({
                    ...customIntegration,
                    appId: e.target.value
                  })
                }
              />
            </div>
          )}
          {customIntegration.connType === ConnectionType.SelfHostedGithub && (
            <div className='mt-6'>
              <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
                API URL (Optional)
              </div>
              <div className='mb-2 text-xs text-[rgba(0,0,0,0.45)]'>
                Optional: Specify if your GitHub Enterprise Server uses a different hostname for API
                calls (e.g., api.github.example.com).
              </div>
              <InputArea
                singleLine
                fullWidth
                placeholder='e.g. https://github.example.com/api/v3 or https://api.github.example.com'
                isDisabled={customIntegrationModalLoading}
                value={customIntegration.apiUrl || ''}
                onChange={e =>
                  setCustomIntegration({
                    ...customIntegration,
                    apiUrl: e.target.value
                  })
                }
              />
            </div>
          )}
          {customIntegration.connType === ConnectionType.SelfHostedGithub && (
            <div className='mt-6'>
              <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
                GitHub App Private Key (PEM)
              </div>
              <UploadFile
                variant='without-toggle'
                uploadState={privateKeyUploadState}
                mainTitle='Upload Private Key'
                description=''
                onFileSelect={handlePrivateKeySelect}
                onRemove={handlePrivateKeyRemove}
                onErrorRemove={handlePrivateKeyRemove}
                onDropFile={handlePrivateKeyDrop}
                accept='.pem,.key'
                inputId='selfHostedGithubPrivateKeyUpload'
                disabled={customIntegrationModalLoading}
              />
              {customIntegration.hasPrivateKey && customIntegration.privateKey === '' && (
                <div className='mt-2 text-xs text-[rgba(0,0,0,0.45)]'>
                  A private key is already stored. Upload a new PEM file to replace it or leave this
                  empty to keep the current key.
                </div>
              )}
              {privateKeyError && (
                <div className='mt-2 text-xs text-tokens-status-error-text'>{privateKeyError}</div>
              )}
            </div>
          )}
          {(customIntegration.connType === ConnectionType.SelfHostedGithub ||
            customIntegration.connType === ConnectionType.SelfHostedGitlab) && (
            <div className='mt-6'>
              <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
                Custom SSL CA Certificate (PEM)
              </div>
              <div className='mb-2 text-xs text-[rgba(0,0,0,0.45)]'>
                Optional. Upload a custom CA certificate if your self-hosted instance uses a
                self-signed or custom certificate.
              </div>
              <UploadFile
                variant='without-toggle'
                uploadState={caCertUploadState}
                mainTitle='Upload PEM Certificate'
                description=''
                onFileSelect={handleCaCertSelect}
                onRemove={handleCaCertRemove}
                onErrorRemove={handleCaCertRemove}
                onDropFile={handleCaCertDrop}
                accept='.pem,.crt,.cer'
                inputId='selfHostedCaCertUpload'
                disabled={customIntegrationModalLoading}
              />
              {initialHasServerCaCert && !customIntegration.serverCaCertBase64 && (
                <div className='mt-2 text-xs text-[rgba(0,0,0,0.45)]'>
                  A CA certificate is already stored. Upload a new PEM file to replace it or leave
                  this empty to keep the current certificate.
                </div>
              )}
              {caCertError && (
                <div className='mt-2 text-xs text-tokens-status-error-text'>{caCertError}</div>
              )}
            </div>
          )}
        </div>
        <div className='w-[360px]'>
          <CustomIntegrationOauthForm
            isLoading={customIntegrationModalLoading}
            customIntegration={customIntegration}
            setCustomIntegration={setCustomIntegration}
          ></CustomIntegrationOauthForm>
        </div>
      </div>

      <div
        className='
        w-full h-[68px] py-[16px] px-[32px] flex items-center
       bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2
       justify-end gap-2
      '
      >
        <Button variant='outlined' onClick={onCancel} className='w-[78px] h-9'>
          Cancel
        </Button>
        <Button
          variant='primary'
          className='w-[65px] h-9'
          isDisabled={
            customIntegration.serverUrl === '' ||
            caCertUploadState.status === 'uploading' ||
            caCertUploadState.status === 'error' ||
            privateKeyUploadState.status === 'uploading' ||
            privateKeyUploadState.status === 'error' ||
            (customIntegration.connType === ConnectionType.SelfHostedGithub
              ? customIntegration.appName === '' ||
                customIntegration.appId === '' ||
                (!customIntegration.hasPrivateKey && customIntegration.privateKey === '')
              : customIntegration.clientId === '' || customIntegration.clientSecret === '')
          }
          onClick={() => {
            if (!currentWorkspace?.uuid) {
              console.warn('No workspace selected')
              return
            }
            const workspaceUuid = currentWorkspace.uuid
            const oauthUrl = `/oauth/${customIntegration.connType.toLowerCase()}/login?workspace_uuid=${workspaceUuid}`

            // Open the popup synchronously so it isn't popup-blocked (see
            // useOAuthPopup). Persisting the custom connection runs as
            // beforeOpen — AFTER the window is opened; a failure there closes
            // the popup and surfaces via onError.
            openOAuthPopup(oauthUrl, {
              beforeOpen: async () => {
                await updateCustomConnection(workspaceUuid, customIntegration)
                store.dispatch(setConnType(customIntegration.connType))
                return true
              },
              onSuccess: data => {
                console.log('OAuth success:', data)
                // Navigate to index modal after successful connection
                if (onOAuthSuccess) {
                  onOAuthSuccess(customIntegration.connType.toLowerCase())
                  onCancel()
                } else {
                  // Fallback: reload if no callback provided
                  window.location.reload()
                }
              },
              onError: error => {
                console.error('OAuth error:', error)
                alert(`Failed to connect: ${error.error}`)
              }
            })
          }}
        >
          Save
        </Button>
      </div>
    </>
  )
}

const CustomIntegrationOauthForm = ({
  isLoading,
  customIntegration,
  setCustomIntegration
}: CustomIntegrationOauthFormProps) => {
  const [oauthValues, setOauthValues] = useState({
    clientId: '',
    clientSecret: '',
    redirectUrl: ''
  })
  useEffect(() => {
    setOauthValues({
      clientId: customIntegration.clientId,
      clientSecret: customIntegration.clientSecret,
      redirectUrl: customIntegration.redirectUrl
    })
  }, [customIntegration.clientId, customIntegration.clientSecret, customIntegration.redirectUrl])

  const isSelfHostedGithub = customIntegration.connType === ConnectionType.SelfHostedGithub

  return (
    <div className='w-[360px]'>
      {!isSelfHostedGithub && (
        <>
          <div className='mb-4'>
            <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
              Client Id
            </div>
            <InputArea
              singleLine
              className='w-[360px] h-12 py-[8px]'
              placeholder='Input Your Application Client Id'
              isDisabled={isLoading}
              value={oauthValues.clientId}
              onChange={e => {
                setOauthValues(prev => ({
                  ...prev,
                  clientId: e.target.value
                }))
                setCustomIntegration({
                  ...customIntegration,
                  clientId: e.target.value
                })
              }}
            />
          </div>
          <div className='mb-4'>
            <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
              Client Secret
            </div>
            <InputArea
              singleLine
              className='w-[360px] h-12 py-[8px]'
              placeholder='Input Your Application Client Secret'
              isDisabled={isLoading}
              value={oauthValues.clientSecret}
              onChange={e => {
                setOauthValues(prev => ({
                  ...prev,
                  clientSecret: e.target.value
                }))
                setCustomIntegration({
                  ...customIntegration,
                  clientSecret: e.target.value
                })
              }}
            />
          </div>
        </>
      )}
      <div className=''>
        <div className='mb-2 font-mono font-semibold text-sm text-[rgba(0,0,0,0.65)]'>
          Redirect Url
        </div>
        <div className='w-[360px]'>
          <CopySnippet
            text={oauthValues.redirectUrl}
            code={oauthValues.redirectUrl}
            className='h-[68px]'
          />
        </div>
      </div>
    </div>
  )
}
